<template>
  <div class="sm:col-span-full" v-if="adminView">
    <dt class="text-sm font-medium text-gray-500">Bifogade dokument</dt>
    <div
      v-if="isPreviewModalVisible && !isPdfFile(currentPreviewFile) && !getConvertedPdfFile(currentPreviewFile)"
      class="preview-modal pb-2"
    >
      <!-- Förhandsgranskning -->
      <div>
        <div class="flex flex-row-reverse">
          <button class="text-xl" @click="closePreviewModal">&times;</button>
        </div>
        <!-- Förhandsgranskningsinnehållet -->
        <template v-if="isImageFile(currentPreviewFile)">
          <img
            :src="previewFileUrl"
            alt="Preview"
            style="width: 100%; height: auto"
          />
        </template>
        <template v-else>
          <iframe :src="previewFileUrl" width="100%" height="600px"></iframe>
        </template>
      </div>
    </div>
    <!-- PDF Modal - rendered outside the conditional div -->
    <SecurePdfViewerModal
      v-if="currentPreviewFile && (isPdfFile(currentPreviewFile) || getConvertedPdfFile(currentPreviewFile))"
      :pdfUrl="previewFileUrl"
      :visible="isPreviewModalVisible"
      :filename="currentPreviewFile?.filename"
      :isConverted="isShowingConvertedFile"
      @close="closePreviewModal"
    />
    <dd
      :style="{ color: themedText?.otherText }"
      class="mt-1 text-sm text-gray-900"
    >
      <ul
        role="list"
        class="divide-y divide-gray-200 rounded-md border border-gray-200"
      >
        <li
          v-for="attachment in filteredFiles"
          :key="attachment.id"
          class="flex items-center justify-between py-3 pl-3 pr-4 text-sm"
        >
          <div class="flex w-0 flex-1 items-center">
            <i
              class="fas fa-paperclip mr-2 flex-shrink-0 items-center text-gray-400"
              aria-hidden="true"
            />
            <span class="ml-2 w-0 flex-1 truncate">{{
              attachment.filename
            }}</span>
          </div>
          <div class="ml-4 flex-shrink-0" v-if="editMode">
            <button
              @click="handleFileRemove(attachment)"
              class="font-medium text-red-600 hover:text-red-500"
            >
              Radera
            </button>
          </div>
          <div class="ml-4 flex-shrink-0">
            <button
              @click.prevent="(event) => openPreviewModal(attachment, event)"
              class="font-medium text-orange-1 hover:text-orange-3 mr-3"
            >
              <i class="fa-solid fa-magnifying-glass"></i>
            </button>
            <a
              href="#"
              @click.prevent="downloadFile(attachment)"
              class="font-medium text-blue-1 hover:text-blue-3"
            >
              <i class="fa-solid fa-file-arrow-down"></i>
            </a>
          </div>
        </li>
      </ul>
      <div
        class="relative mt-4 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6"
        :class="{
          'bg-gray-100 text-darkGray-2 cursor-not-allowed pointer-events-none':
            disabled,
        }"
        :id="model.key"
        @dragover.prevent
        @drop="handleFileDrop"
        v-if="editMode"
      >
        <div class="space-y-1 text-center">
          <div class="flex text-sm text-gray-600">
            <label for="file-upload" class="relative">
              <span
                class="cursor-pointer rounded-md font-medium text-blue-1 focus-within:outline-none hover:text-blue-2 inline-block"
                >{{
                  files.length > 0
                    ? "Ladda upp ytterligare fil(er)"
                    : "Ladda upp fil(er)"
                }}</span
              >
              <p class="pl-1 inline-block">eller dra och släpp</p>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                class="sr-only"
                :class="{
                  'bg-gray-100 text-darkGray-2 cursor-not-allowed pointer-events-none':
                    disabled,
                }"
                ref="fileInput"
                @change="handleFileUpload"
              />
            </label>
          </div>

          <p class="text-xs text-gray-500">filstorlek max 5MB</p>
        </div>
        <div
          :id="'icon-error_' + model.key"
          class="hidden absolute top-0 bottom-0 right-0 left-auto w-[60px] flex flex-row justify-center items-center"
          @mouseenter="showErrorMessage"
          @mouseleave="hideErrorMessage"
        >
          <i class="text-red-2 fa-solid fa-circle-info"></i>
        </div>
      </div>
    </dd>
    <span class="mt-2 text-sm text-gray-900" v-if="filteredFiles.length === 0"
      >Inga bifogade filer.</span
    >
  </div>

  <div v-else>
    <span
      :style="{ color: themedText?.otherText }"
      class="block uppercase tracking-wide text-darkGray-1 text-xs font-bold mb-1"
      :for="model.key"
    >
      {{ model.label }}
    </span>
    <div
      class="relative mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6"
      :class="{
        'bg-gray-100 text-darkGray-2 cursor-not-allowed pointer-events-none':
          disabled,
      }"
      :id="model.key"
      @dragover.prevent
      @drop="handleFileDrop"
      v-if="!admin"
    >
      <div class="space-y-1 text-center">
        <div class="flex text-sm text-gray-600">
          <label for="file-upload" class="relative">
            <span
              :style="{ color: themedText?.workAtText }"
              class="cursor-pointer rounded-md font-medium text-blue-1 focus-within:outline-none hover:text-blue-2 inline-block"
              >{{
                files.length > 0
                  ? "Ladda upp ytterligare fil(er)"
                  : "Ladda upp fil(er)"
              }}</span
            >
            <p class="pl-1 inline-block">eller dra och släpp</p>
            <input
              id="file-upload"
              name="file-upload"
              type="file"
              class="sr-only"
              :class="{
                'bg-gray-100 text-darkGray-2 cursor-not-allowed pointer-events-none':
                  disabled,
              }"
              ref="fileInput"
              @change="handleFileUpload"
            />
          </label>
        </div>

        <p class="text-xs text-gray-500">filstorlek max 5MB</p>
      </div>
      <div
        :id="'icon-error_' + model.key"
        class="hidden absolute top-0 bottom-0 right-0 left-auto w-[60px] flex flex-row justify-center items-center"
        @mouseenter="showErrorMessage"
        @mouseleave="hideErrorMessage"
      >
        <i class="text-red-2 fa-solid fa-circle-info"></i>
      </div>
    </div>
    <div v-show="showingErrorMessage" class="relative z-50">
      <div
        class="absolute top-0 left-0 right-0 bottom-auto bg-white rounded-md p-2 shadow-md"
        :id="'outer-error_' + model.key"
      >
        <p
          class="font-medium text-sm text-red-2"
          :id="'error_' + model.key"
        ></p>
      </div>
    </div>
    <ul
      v-if="files.length > 0"
      :class="{
        'flex flex-col gap-1 text-sm font-medium bg-blue-4 p-4 rounded leading-tight mt-2':
          !disabled,
        'flex flex-col gap-1 text-sm font-medium bg-gray-2 p-4 rounded leading-tight mt-2':
          disabled,
      }"
    >
      <span class="text-darkGray-1 font-semibold mb-1">Bifogade filer:</span>
      <li
        v-for="file in files"
        :key="file.filename"
        class="flex gap-2 items-center"
      >
        <a href="#" @click.prevent="downloadFile(file)">{{ file.filename }} </a>

        <button
          v-if="!disabled"
          @click="handleFileRemove(file)"
          class="flex gap-1 items-center text-red-2 hover:text-red-1"
        >
          <i class="fa-solid fa-trash text-xs"></i>
        </button>
      </li>
    </ul>
  </div>
</template>
<script setup lang="ts">
import { defineProps, shallowRef, ref, onMounted, computed } from "vue";
import { defineEmits } from "@vue/runtime-core";
import { AttachmentRequest, DynamicProperty } from "@/models/dtos";
import { useRoute } from "vue-router";
import { TYPES } from "@/types";
import { useStore } from "vuex";
import axios from "axios";
import { MainContainer } from "@/models/MainContainer";
import { Profile } from "@/models/Profile";
import SecurePdfViewerModal from "@/features/shared/components/ui/SecurePdfViewerModal.vue";
const store = useStore();
const containerByDNS = ref<Profile>(null);

const emit = defineEmits(["change"]);
const props = defineProps({
  model: {
    required: false,
  },
  type: String,
  positionId: Number,
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  admin: {
    type: Boolean,
    required: false,
    default: false,
  },
  adminView: {
    type: Boolean,
    required: false,
    default: false,
  },
  editMode: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const route = useRoute();
const files = ref([]);
const fileInput = ref(null);
const showingErrorMessage = ref(false);
let model = shallowRef(props.model as DynamicProperty);

const showErrorMessage = () => {
  showingErrorMessage.value = true;
};

const hideErrorMessage = () => {
  showingErrorMessage.value = false;
};

const handleFileDrop = (event: any) => {
  event.preventDefault();
  const newFiles = [] as AttachmentRequest[];

  event.dataTransfer.files.forEach((element: any) => {
    // validate file size
    if (element.size > 5000000) {
      alert("Filen är för stor");
      return;
    }

    const file = new AttachmentRequest();
    file.filename = element.name;
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      file.data = fileEvent.target.result.toString();
    };
    reader.readAsDataURL(element);
    newFiles.push(file);
  });

  files.value = [...files.value, ...newFiles];

  model.value.data = files;
  emit("change", model.value);
};

const handleFileUpload = () => {
  const newFiles = [] as AttachmentRequest[];

  fileInput.value.files.forEach((element: any) => {
    // validate file size
    if (element.size > 5000000) {
      alert("Filen är för stor");
      return;
    }

    const file = new AttachmentRequest();
    file.filename = element.name;
    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      file.data = fileEvent.target.result.toString();
    };
    reader.readAsDataURL(element);
    newFiles.push(file);
  });

  files.value = [...files.value, ...newFiles];

  model.value.data = files;
  emit("change", model.value);
};

const handleFileRemove = (file: any) => {
  files.value.splice(files.value.indexOf(file), 1);

  model.value.data = files;
  emit("change", model.value);
};
onMounted(() => {
  if (model.value.data) {
    files.value = model.value.data;
  }
});

function getUrl(file: any) {
  const url =
    TYPES.BaseURL +
    "/api/dispatcher/storage/application/" +
    route.params.applicantId +
    "/files/" +
    file.url;
  return url;
}

// ANSKNI-130: the download endpoint now requires auth, so fetch the file with the
// Bearer token and hand the UI a local blob URL instead of a tokenless href/src.
async function fetchFileBlobUrl(file: any): Promise<string> {
  const response = await axios.get(getUrl(file), {
    responseType: "blob",
    headers: { Authorization: "Bearer " + store.getters.token },
  });
  return URL.createObjectURL(response.data as Blob);
}

async function downloadFile(file: any) {
  try {
    const blobUrl = await fetchFileBlobUrl(file);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = file.filename || "bilaga";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  } catch (error) {
    console.error("Kunde inte ladda ner filen:", error);
  }
}

onMounted(async () => {
  await store.dispatch("loadContainerTheme");
  try {
    await store.dispatch("loadContainerTheme");
    containerByDNS.value = new Profile(
      new MainContainer(store.getters.publicContainer)
    );
    // isLoaded.value = true;
  } catch (error) {
    console.error("Error loading public container:", error);
  }
});

const isPreviewModalVisible = ref(false);
const previewFileUrl = ref("");
const currentPreviewFile = ref(null);
const isShowingConvertedFile = ref(false);

const openPreviewModal = async (file: any, event: Event) => {
  event.preventDefault();

  currentPreviewFile.value = file;

  // Check if there's a converted PDF version available for preview
  const convertedPdf = getConvertedPdfFile(file);

  try {
    if (convertedPdf && convertedPdf !== file) {
      // Use converted PDF for preview and mark it as converted
      previewFileUrl.value = await fetchFileBlobUrl(convertedPdf);
      isShowingConvertedFile.value = true;
      isPreviewModalVisible.value = true;
    } else if (isImageFile(file) || isPdfFile(file)) {
      previewFileUrl.value = await fetchFileBlobUrl(file);
      isShowingConvertedFile.value = false;
      isPreviewModalVisible.value = true;
    } else {
      // Not an image/PDF (e.g. a Word document): the authenticated endpoint cannot
      // be rendered by an external viewer, so download the file instead.
      await downloadFile(file);
    }
  } catch (error) {
    console.error("Kunde inte förhandsgranska filen:", error);
  }
};

const closePreviewModal = () => {
  isPreviewModalVisible.value = false;
  isShowingConvertedFile.value = false;
  if (previewFileUrl.value && previewFileUrl.value.startsWith("blob:")) {
    URL.revokeObjectURL(previewFileUrl.value);
    previewFileUrl.value = "";
  }
};

const isImageFile = (file: any) => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp"]; // Lägg till fler bildtyper om det behövs
  const extension = file.filename.split(".").pop().toLowerCase();
  return imageExtensions.includes(extension);
};

const isPdfFile = (file: any) => {
  // Kontrollera filändelsen istället för MIME-typ
  const pdfExtensions = ["pdf"]; // Lägg till fler PDF-typer om det behövs
  const extension = file.filename.split(".").pop().toLowerCase();
  return pdfExtensions.includes(extension);
};

const themedText = computed(() => store.getters.getContainerThemedText);

// Filter out .converted.pdf files from display
const filteredFiles = computed(() => {
  return files.value.filter((file: any) => !file.filename.endsWith('.converted.pdf'));
});

// Helper function to find the converted PDF version of a file
const getConvertedPdfFile = (originalFile: any) => {
  if (isPdfFile(originalFile)) {
    return originalFile; // Already a PDF, no need for conversion
  }
  
  const baseFilename = originalFile.filename.replace(/\.[^/.]+$/, ''); // Remove extension
  const convertedFilename = baseFilename + '.converted.pdf';
  
  return files.value.find((file: any) => file.filename === convertedFilename);
};
</script>
